import React, { useState } from "react";
import {
  Box,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
} from "@mui/material";
import { DataGridPro, GridColDef } from "@mui/x-data-grid-pro";
import ReactCountryFlag from "react-country-flag";
import AddIcon from "@mui/icons-material/Add";
import { useRoster } from "../../../contexts/RosterContext";
import { PlayerProfileModal } from "./PlayerProfileModal";

export const Roster = () => {
  const { selectedRoster, rosterPlayers, isLoading, error, fetchRosterPlayers, removeRosterPlayer } = useRoster();
  const [selectedPlayer, setSelectedPlayer] = useState<any | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isNewPlayer, setIsNewPlayer] = useState<boolean>(false);

  // Handle add new player button click
  const handleAddNewPlayer = () => {
    if (!selectedRoster) return;

    setIsNewPlayer(true);
    setSelectedPlayer({
      membership: { status: "ACTIVE" },
      rosterId: selectedRoster.id,
    });
    setIsModalOpen(true);
  };

  // Get status color based on player status
  const getStatusColor = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return "success";
      case "INJURED":
        return "error";
      case "REDSHIRT":
        return "warning";
      case "PRACTICE":
        return "info";
      case "INACTIVE":
        return "error";
      default:
        return "default";
    }
  };

  const columns: GridColDef[] = [
    {
      field: "jerseyNumber",
      headerName: "#",
      width: 70,
      headerAlign: "center",
      align: "center",
      filterable: true,
    },
    {
      field: "name",
      headerName: "Name",
      width: 200,
      filterable: true,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {params.row.country && (
            <ReactCountryFlag
              countryCode={params.row.country}
              svg
              style={{
                width: "1.2em",
                height: "1.2em",
              }}
              title={params.row.country}
            />
          )}
          <span>{params.value}</span>
        </Box>
      ),
    },
    {
      field: "off_pos",
      headerName: "Offense",
      width: 120,
      headerAlign: "center",
      align: "center",
      filterable: true,
    },
    {
      field: "def_pos",
      headerName: "Defense",
      width: 120,
      headerAlign: "center",
      align: "center",
      filterable: true,
    },
    {
      field: "positionGroup",
      headerName: "Position Group",
      width: 150,
      headerAlign: "center",
      align: "center",
      filterable: true,
    },
    {
      field: "gradYear",
      headerName: "Class",
      width: 100,
      headerAlign: "center",
      align: "center",
      filterable: true,
    },
    {
      field: "status",
      headerName: "Status",
      width: 200,
      headerAlign: "center",
      align: "center",
      filterable: true,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getStatusColor(params.value) as any}
          size="small"
        />
      ),
    },
  ];

  const formatPositionGroup = (value: string) => {
    return value
      .split("_")
      .map(
        (word: string) =>
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      )
      .join(" ");
  };

  // Transform roster players into rows for DataGrid
  const rows = rosterPlayers.map((membership) => ({
    id: membership.id,
    jerseyNumber: membership.jersey_number,
    name: membership.player.name,
    country: membership.player.country_code,
    off_pos: membership.player.offensive_position || "-",
    def_pos: membership.player.defensive_position || "-",
    positionGroup: formatPositionGroup(
      membership.player.position_group || "UNKNOWN"
    ),
    gradYear: membership.player.grad_year || "-",
    status: membership.status,
  }));

  // Handle player row click
  const handlePlayerClick = (params: any) => {
    // Find the selected player data from rosterPlayers
    const playerMembership = rosterPlayers.find(
      (membership) => membership.id === params.id
    );

    if (playerMembership) {
      // First close the modal and reset selected player to avoid stale data
      setIsModalOpen(false);
      setSelectedPlayer(null);
      setIsNewPlayer(false);

      // Then set the new player after a brief delay to ensure React state is updated
      setTimeout(() => {
        setSelectedPlayer({
          membership: playerMembership,
          rosterId: selectedRoster?.id,
        });
        setIsModalOpen(true);
      }, 50);
    }
  };

  // Handle modal close
  const handleCloseModal = () => {
    setIsModalOpen(false);
    if (isNewPlayer) {
      setSelectedPlayer(null);
      setIsNewPlayer(false);
    }
  };

  // Handle player update after editing
  const handlePlayerUpdate = () => {
    // Refresh the roster players after update
    if (selectedRoster) {
      fetchRosterPlayers(selectedRoster.id);
    }
    setIsModalOpen(false);
    setIsNewPlayer(false);
    setSelectedPlayer(null);
  };

  // Handle remove player from roster
  const handleRemoveRosterPlayer = async (rosterId: number, playerId: number) => {
    const result = await removeRosterPlayer(rosterId, playerId);
    if (result) {
      handlePlayerUpdate();
    }
  };

  const tableWidth = columns.reduce(
    (acc, column) => acc + (column?.width || 0),
    0
  );

  return (
    <Box sx={{ p: 1, maxWidth: tableWidth + 40, width: "100%" }}>
      {/* Header with Add Player Button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          mb: 1,
        }}
      >
        <IconButton
          onClick={handleAddNewPlayer}
          color="primary"
          title="Add Player"
          size="small"
        >
          <AddIcon />
        </IconButton>
      </Box>

      {/* Loading and Error States */}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Roster Table using DataGrid */}
      {!isLoading && !error && rosterPlayers.length > 0 && (
        <>
          <Box sx={{ width: "100%", height: "calc(100vh - 180px)" }}>
            <DataGridPro
              rows={rows}
              columns={columns}
              initialState={{
                sorting: {
                  sortModel: [{ field: "jerseyNumber", sort: "asc" }],
                },
              }}
              hideFooter
              disableRowSelectionOnClick
              onRowClick={handlePlayerClick}
              disableColumnMenu={false}
              density="compact"
              sx={{
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "primary.main",
                  color: "white",
                  fontWeight: "bold",
                  position: "sticky",
                  top: 0,
                  zIndex: 5,
                },
                "& .MuiDataGrid-row:hover": {
                  cursor: "pointer",
                  backgroundColor: "rgba(170, 163, 163, 0.39)",
                },
                "& .MuiDataGrid-cell:last-of-type": {
                  paddingRight: "8px",
                },
                "& .MuiDataGrid-columnHeader": {
                  padding: "0 8px",
                },
                height: "100%",
                width: "100%",
                border: "none",
                "& .MuiDataGrid-main": {
                  overflow: "auto",
                },
                "& .MuiDataGrid-virtualScroller": {
                  overflow: "auto",
                  "&::-webkit-scrollbar": {
                    width: "8px",
                    height: "8px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "rgba(0,0,0,.2)",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "rgba(0,0,0,.05)",
                  },
                },
              }}
            />
          </Box>
        </>
      )}

      {!isLoading &&
        !error &&
        rosterPlayers.length === 0 && (
          <Alert severity="info" sx={{ mt: 1 }}>
            No players found in this roster.
          </Alert>
        )}

      {/* Player Profile Modal */}
      {selectedPlayer && (
        <PlayerProfileModal
          open={isModalOpen}
          onClose={handleCloseModal}
          player={isNewPlayer ? {} : selectedPlayer.membership.player}
          membership={selectedPlayer.membership}
          rosterId={selectedPlayer.rosterId}
          onUpdate={handlePlayerUpdate}
          onRemove={handleRemoveRosterPlayer}
        />
      )}
    </Box>
  );
};
